import React, { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { AlertTriangle, Home, RefreshCcw, LogOut } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Link } from "react-router-dom"
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { clearAppStorage, navigateToAppPath, reloadApp } from '../platform/runtime';
import SupportContactLinks from '../components/SupportContactLinks';

const Error: React.FC = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = () => {
    setIsRefreshing(true)
    reloadApp()
  }

  const handleLogout = async () => {
    clearAppStorage();

    try {
      await auth.signoutRedirect();
    } catch {
      navigateToAppPath('/', true);
    }
  }

  return (
    <IonPage>
      <IonContent
        fullscreen
        className="bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100"
      >
        <div
          className="mx-auto flex w-full max-w-2xl items-start justify-center px-4 sm:items-center sm:px-6"
          style={{
            minHeight: '100dvh',
            paddingTop: 'max(1rem, calc(var(--ion-safe-area-top, env(safe-area-inset-top)) + 0.75rem))',
            paddingBottom: 'max(1.5rem, calc(var(--ion-safe-area-bottom, env(safe-area-inset-bottom)) + 1.25rem))',
          }}
        >
          <Card className="w-full border-red-200 bg-white/95 shadow-xl backdrop-blur-sm">
            <CardHeader className="space-y-4 px-4 pb-5 pt-6 text-center sm:px-8 sm:pb-6 sm:pt-8">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 sm:h-20 sm:w-20">
                    <AlertTriangle className="h-8 w-8 text-red-600 sm:h-10 sm:w-10" />
                  </div>
                  <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 sm:h-6 sm:w-6">
                    <span className="text-[10px] font-bold text-amber-950 sm:text-xs">!</span>
                  </div>
                </div>
              </div>
              <div>
                <CardTitle className="mb-2 text-2xl font-bold text-slate-800 sm:text-3xl">
                  {t('errorUnexpectedTitle')}
                </CardTitle>
                <CardDescription className="mx-auto max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                  {t('errorUnexpectedDescription')}
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-6 sm:space-y-6 sm:px-8 sm:pb-8">
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild className="h-11 flex-1 bg-action-700 text-white hover:bg-action-800">
                  <Link to="/">
                    <Home className="mr-2 h-4 w-4" />
                    {t('errorBackHome')}
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="h-11 flex-1 border-action-300 bg-transparent text-action-700 hover:bg-action-50"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <>
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                      {t('errorRefreshing')}
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="mr-2 h-4 w-4" />
                      {t('errorTryAgain')}
                    </>
                  )}
                </Button>
              </div>

              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  className="h-10 w-full text-slate-600 hover:bg-slate-100 hover:text-slate-800 sm:w-auto"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('signOut')}
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-slate-500">{t('errorNeedHelp')}</span>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-center text-sm font-semibold text-slate-700">
                  {t('errorContactSupport')}
                </h3>
                <SupportContactLinks />
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-center text-xs leading-relaxed text-slate-600">
                  {t('errorSupportAvailability')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default Error;
